import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../utils'

export const baseInputStyles = [
  'text-lg text-input-foreground',
  'flex h-13 w-full rounded-md px-3 py-1',

  // Disabled state
  'disabled:cursor-not-allowed disabled:hover:bg-input-secondary-background disabled:opacity-50',

  // Hover state
  'hover:bg-input-background hover:border-input-background',

  'file:border-0 file:bg-transparent file:text-sm file:font-medium',
].join(' ')

const defaultInputStyle = [
  'bg-input-secondary-background border border-input-secondary-border outline-0 outline-input-focus-shadow',

  // Error state
  'aria-invalid:drop-shadow-(--input-invalid-shadow) aria-invalid:border-input-invalid aria-invalid:border',
  // Focus
  'focus-visible:ring focus-visible:ring-input-focus-border focus-visible:bg-input-background focus-visible:outline-3 focus-visible:outline-offset-1',

  // Populated
  '[data-is-dirty=true]:bg-input-background [data-is-dirty=true]:border-input-background',
].join(' ')
//
export const inputStyles = `${baseInputStyles} ${defaultInputStyle}`

export const inputVariants = cva(baseInputStyles, {
  variants: {
    size: {
      default: '',
      lg: 'text-2xl py-4 h-auto',
    },
    variant: {
      default: defaultInputStyle,

      ghost: 'bg-transparent border-0 focus-visible:outline-hidden',
      inverse: 'bg-background border-border-dark border outline-0 outline-input-focus-shadow hover:bg-background hover:border-border-dark aria-invalid:drop-shadow-(--input-invalid-shadow) focus-visible:ring focus-visible:ring-input-focus-border focus-visible:outline-3 focus-visible:outline-offset-1 aria-invalid:border-input-invalid aria-invalid:border',
    },
  },

  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
})

export type InputProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'size'> &
  VariantProps<typeof inputVariants>

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, variant, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          inputVariants({ size, variant }),

          className,
        )}
        data-is-dirty={!!props.value}
        ref={ref}
        {...props}
        value={props.value ?? ''}
      />
    )
  },
)
Input.displayName = 'Input'

export { Input }
